import React, { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import Lenis from 'lenis';
import Nav           from './components/Nav';
import Hero          from './components/Hero';
import Manifesto     from './components/Manifesto';
import AnimSequence  from './components/AnimSequence';
import ModularDesign from './components/ModularDesign';
import FieldService  from './components/FieldService';
import UseCases      from './components/UseCases';
import Specs         from './components/Specs';
import Contact       from './components/Contact';
import Footer        from './components/Footer';

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,   // native momentum scroll on touch/mobile
      touchAction: 'pan-y', // let browser handle vertical touch scroll
    });

    let raf;
    function loop(time) {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <>
      {/* Skip-to-main — keyboard accessibility */}
      <Box as="a" href="#main-content"
        position="fixed" top={2} left={2} zIndex={9999}
        bg="oid.accent" color="white"
        px={4} py={2} borderRadius="md"
        fontWeight={600} fontSize="sm"
        transform="translateY(-120%)"
        _focusVisible={{ transform: 'translateY(0)', outline: '2px solid white', outlineOffset: '2px' }}
        sx={{ '&:focus': { transform: 'translateY(0)' }, transition: 'transform 0.2s ease' }}>
        Skip to main content
      </Box>

      {/* Fixed dot-grid texture */}
      <Box aria-hidden="true" position="fixed" inset={0} zIndex={0} pointerEvents="none"
        sx={{
          backgroundImage: 'radial-gradient(circle, rgba(22,33,58,0.065) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />

      <Nav />

      {/* Section rhythm: #16213a → #efefef → #000 → #fff → #000 → #efefef → #000 → #fff → #16213a → #000 */}
      <Box as="main" id="main-content" position="relative" zIndex={1}>
        <Hero />

        <Manifesto />

        <AnimSequence
          id="assembly"
          duration={2}
          frames={36}
          imageSequenceUrl={(i) => `/image-sequences/Assembly/assembly${String(i).padStart(4, '0')}.jpg`}
          fitWidth
          captionIndex="01. Assembly"
          captionTitle="Every component, a purpose."
          captionBody="Watch O-ID assemble module by module, each part engineered to connect, disconnect, and reconnect in the field without specialist tools."
        />

        <ModularDesign />

        <AnimSequence
          id="field-service"
          duration={4}
          frames={124}
          imageSequenceUrl={(i) => `/image-sequences/Field-sevicable/O-ID-Field-Servicable${String(i).padStart(4, '0')}.jpg`}
          captionIndex="03. Field Service"
          captionTitle="Maintained in minutes."
          captionBody="No service depot required. Any trained operator can replace a failed module on-site and return O-ID to full operation immediately."
        />

        <FieldService />

        <AnimSequence
          id="deployment"
          duration={6}
          frames={191}
          imageSequenceUrl={(i) => `/image-sequences/Adaptable/O-ID-Adaptable${String(i).padStart(4, '0')}-opti.jpg`}
          captionIndex="05. Infinite Possibilities"
          captionTitle="One platform, infinite configurations."
          captionBody="From manufacturing floors to hazardous environments, O-ID adapts its physical form to match the mission, not the other way around."
        />

        <UseCases />

        <Specs />

        <Contact />

        <Footer />
      </Box>
    </>
  );
}

export default App;
